import { useState } from "react";
import { sleepAPI } from "../lib/api";
import { generateSleepReport } from "../lib/reportGenerator";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Checkbox } from "./ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Separator } from "./ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { X, Moon, Sun, Clock, Bed, CalendarDays, User, Briefcase, Download, CheckCircle, Coffee, Phone } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_16338d16-af9f-4ec6-a8f3-cd6da42efe59/artifacts/7xtaj2mx_WhatsApp%20Image%202026-03-02%20at%203.01.19%20PM%20%281%29.jpeg";

const SleepDiaryForm = ({ onClose, onSubmit }) => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submittedEntry, setSubmittedEntry] = useState(null);
  
  // Time picker state
  const [bedTimeHour, setBedTimeHour] = useState("10");
  const [bedTimeMinute, setBedTimeMinute] = useState("00");
  const [bedTimePeriod, setBedTimePeriod] = useState("PM");
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    subject_name: "",
    age: "",
    phone_number: "",
    emergency_contact: "",
    day_type: "",
    consumption_items: [],
    consumption_timing: "",
    bed_time: "",
    sleep_latency: "",
    wake_count: "",
    wake_duration: "",
    final_wake_time: "",
    get_up_time: "",
    nap: "",
    quality: ""
  });

  // Phone number validation
  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^[0-9]{10,15}$/;
    return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Update bed_time when time picker changes
  const updateBedTime = (hour, minute, period) => {
    const formattedTime = `${hour}:${minute} ${period}`;
    updateField("bed_time", formattedTime);
  };

  const toggleConsumptionItem = (item) => {
    setFormData(prev => {
      let newItems = [...prev.consumption_items];
      
      if (item === "None") {
        // If None is selected, clear all other items
        newItems = ["None"];
      } else {
        // Remove "None" if selecting other items
        newItems = newItems.filter(i => i !== "None");
        
        if (newItems.includes(item)) {
          newItems = newItems.filter(i => i !== item);
        } else {
          newItems.push(item);
        }
      }
      
      // Clear timing if no consumption items or only None
      const newTiming = (newItems.length === 0 || (newItems.length === 1 && newItems[0] === "None")) 
        ? "" 
        : prev.consumption_timing;
      
      return { ...prev, consumption_items: newItems, consumption_timing: newTiming };
    });
  };

  const showConsumptionTiming = formData.consumption_items.length > 0 && 
    !formData.consumption_items.includes("None");

  const handleSubmit = async () => {
    // Validate required fields (consumption is optional)
    const requiredFields = ["date", "subject_name", "age", "phone_number", "day_type", "bed_time", 
      "sleep_latency", "wake_count", "wake_duration", "final_wake_time", "get_up_time", "nap", "quality"];
    const missingFields = requiredFields.filter(f => !formData[f]);
    
    if (missingFields.length > 0) {
      toast.error("Please complete all required fields");
      return;
    }

    // Validate phone number
    if (!validatePhoneNumber(formData.phone_number)) {
      toast.error("Please enter a valid phone number (10-15 digits)");
      return;
    }
    
    // Validate consumption timing if items selected
    if (showConsumptionTiming && !formData.consumption_timing) {
      toast.error("Please select when you consumed the items");
      return;
    }

    setLoading(true);
    try {
      const response = await sleepAPI.createEntry({
        ...formData,
        age: parseInt(formData.age),
        consumption_items: formData.consumption_items.length > 0 ? formData.consumption_items : ["None"]
      });
      
      const entry = response.data;
      setSubmittedEntry(entry);
      
      // Auto-generate and download PDF report (now async)
      try {
        const fileName = await generateSleepReport(entry);
        toast.success(`Report downloaded: ${fileName}`);
      } catch (pdfError) {
        console.error("PDF generation failed:", pdfError);
        toast.error("Could not generate PDF, but entry was saved");
      }
      
      // Show success screen
      setShowSuccess(true);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save entry");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (submittedEntry) {
      onSubmit(submittedEntry);
    }
    onClose();
  };

  const handleDownloadAgain = async () => {
    if (submittedEntry) {
      try {
        await generateSleepReport(submittedEntry);
        toast.success("Report downloaded again");
      } catch (error) {
        toast.error("Failed to download report");
      }
    }
  };

  const RadioOption = ({ value, label, icon: Icon, field }) => (
    <div>
      <RadioGroupItem value={value} id={`${field}-${value}`} className="peer sr-only" />
      <Label
        htmlFor={`${field}-${value}`}
        className="flex items-center gap-3 p-3 rounded-lg border-2 border-slate-200 bg-white hover:bg-slate-50 hover:border-primary/50 peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 cursor-pointer transition-all"
        data-testid={`${field}-${value.replace(/\s+/g, '-').toLowerCase()}`}
      >
        {Icon && <Icon className="h-5 w-5 text-slate-500" />}
        <span className="text-sm">{label}</span>
      </Label>
    </div>
  );

  const totalSteps = 6;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-fadeIn">
        
        {/* Success Screen */}
        {showSuccess && submittedEntry ? (
          <>
            <div className="p-8 text-center">
              {/* Logo Header */}
              <div className="mb-6 pb-4 border-b border-slate-200">
                <img 
                  src={LOGO_URL} 
                  alt="SleepMed Logo" 
                  className="h-14 w-auto mx-auto mb-2"
                  data-testid="success-logo"
                />
                <h1 className="text-xl font-bold text-slate-800">SleepMed</h1>
                <p className="text-xs text-slate-500">Laboratory · Solutions · Education</p>
              </div>
              
              <div className="h-16 w-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4 animate-fadeIn">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Entry Saved Successfully!</h2>
              <p className="text-slate-600 mb-6">Your sleep diary entry has been recorded and your report has been downloaded.</p>
              
              {/* Results Summary */}
              <div className="bg-slate-50 rounded-xl p-6 mb-6 text-left">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Sleep Analysis Results</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-slate-200">
                    <p className="text-sm text-slate-500 mb-1">Total Time in Bed</p>
                    <p className="text-xl font-bold text-slate-900">
                      {Math.floor(submittedEntry.total_time_in_bed_minutes / 60)}h {Math.round(submittedEntry.total_time_in_bed_minutes % 60)}m
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 border border-slate-200">
                    <p className="text-sm text-slate-500 mb-1">Total Sleep Time</p>
                    <p className="text-xl font-bold text-slate-900">
                      {Math.floor(submittedEntry.total_sleep_time_minutes / 60)}h {Math.round(submittedEntry.total_sleep_time_minutes % 60)}m
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 border border-slate-200">
                    <p className="text-sm text-slate-500 mb-1">Sleep Efficiency</p>
                    <p className={`text-xl font-bold ${
                      submittedEntry.sleep_efficiency >= 85 ? 'text-green-600' :
                      submittedEntry.sleep_efficiency >= 70 ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {submittedEntry.sleep_efficiency}%
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 border border-slate-200">
                    <p className="text-sm text-slate-500 mb-1">Classification</p>
                    <p className={`text-xl font-bold ${
                      submittedEntry.sleep_classification === 'Good' ? 'text-green-600' :
                      submittedEntry.sleep_classification === 'Moderate' ? 'text-amber-600' : 'text-red-600'
                    }`}>
                      {submittedEntry.sleep_classification}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-4 justify-center">
                <Button
                  variant="outline"
                  onClick={handleDownloadAgain}
                  className="flex items-center gap-2"
                  data-testid="download-again-btn"
                >
                  <Download className="h-4 w-4" />
                  Download Report Again
                </Button>
                
                <Button
                  onClick={handleClose}
                  className="btn-primary"
                  data-testid="done-btn"
                >
                  Done
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Sleep Diary Entry</h2>
                <p className="text-sm text-slate-500">Step {step} of {totalSteps}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-form-btn">
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Progress Bar */}
            <div className="px-6 py-3 bg-slate-50">
              <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${(step / totalSteps) * 100}%` }}
                ></div>
              </div>
            </div>

            {/* Form Content */}
            <div className="p-6">
              {/* Step 1: Basic Information */}
              {step === 1 && (
                <div className="space-y-6 animate-fadeIn">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">Basic Information</h3>
                      <p className="text-sm text-slate-500">Enter your details</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">Date *</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => updateField("date", e.target.value)}
                    className="input-base"
                    data-testid="date-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">Name of the subject</Label>
                  <Input
                    type="text"
                    placeholder="Your answer"
                    value={formData.subject_name}
                    onChange={(e) => updateField("subject_name", e.target.value)}
                    className="input-base"
                    data-testid="subject-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">Age of the subject</Label>
                  <Input
                    type="number"
                    placeholder="Your answer"
                    value={formData.age}
                    onChange={(e) => updateField("age", e.target.value)}
                    className="input-base"
                    data-testid="age-input"
                  />
                </div>

                <Separator className="my-4" />
                
                {/* Contact Details Section */}
                <div className="space-y-4 bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-primary font-medium">
                    <Phone className="h-4 w-4" />
                    Contact Details
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Phone Number *</Label>
                    <Input
                      type="tel"
                      placeholder="Enter phone number (10-15 digits)"
                      value={formData.phone_number}
                      onChange={(e) => updateField("phone_number", e.target.value.replace(/[^0-9\s\-\(\)]/g, ''))}
                      className="input-base"
                      data-testid="phone-input"
                    />
                    <p className="text-xs text-slate-500">Required - Numbers only</p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Emergency Contact (Optional)</Label>
                    <Input
                      type="tel"
                      placeholder="Enter emergency contact number"
                      value={formData.emergency_contact}
                      onChange={(e) => updateField("emergency_contact", e.target.value.replace(/[^0-9\s\-\(\)]/g, ''))}
                      className="input-base"
                      data-testid="emergency-contact-input"
                    />
                  </div>
                </div>

                <Separator className="my-4" />

                <div className="space-y-3">
                  <Label className="text-slate-700 font-medium">Type of day</Label>
                  <RadioGroup 
                    value={formData.day_type} 
                    onValueChange={(v) => updateField("day_type", v)}
                    className="grid grid-cols-2 gap-3"
                  >
                    <RadioOption value="work" label="work" icon={Briefcase} field="day_type" />
                    <RadioOption value="school" label="school" icon={CalendarDays} field="day_type" />
                    <RadioOption value="day off" label="day off" icon={Sun} field="day_type" />
                    <RadioOption value="vacation" label="vacation" icon={Moon} field="day_type" />
                  </RadioGroup>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Pre-Sleep Consumption */}
          {step === 2 && (
            <div className="space-y-6 animate-fadeIn">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Coffee className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Pre-Sleep Consumption</h3>
                  <p className="text-sm text-slate-500">What did you consume before sleep?</p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="space-y-3">
                  <Label className="text-slate-700 font-medium">Did you consume any of the following before sleep?</Label>
                  <p className="text-sm text-slate-500">Select all that apply</p>
                  
                  <div className="space-y-3">
                    {["Caffeine", "Alcohol", "Heavy Meal", "Nicotine", "None"].map(item => (
                      <div key={item} className="flex items-center space-x-3">
                        <Checkbox
                          id={`consumption-${item}`}
                          checked={formData.consumption_items.includes(item)}
                          onCheckedChange={() => toggleConsumptionItem(item)}
                          data-testid={`consumption-${item.toLowerCase().replace(' ', '-')}`}
                        />
                        <Label 
                          htmlFor={`consumption-${item}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          {item}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {showConsumptionTiming && (
                  <>
                    <Separator />
                    <div className="space-y-3">
                      <Label className="text-slate-700 font-medium">How long before sleep did you consume it?</Label>
                      <RadioGroup 
                        value={formData.consumption_timing} 
                        onValueChange={(v) => updateField("consumption_timing", v)}
                        className="space-y-2"
                      >
                        {[
                          "Less than 30 minutes",
                          "30–60 minutes",
                          "1–2 hours",
                          "More than 2 hours"
                        ].map(option => (
                          <RadioOption key={option} value={option} label={option} field="consumption_timing" />
                        ))}
                      </RadioGroup>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Sleep Onset */}
          {step === 3 && (
            <div className="space-y-6 animate-fadeIn">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Bed className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Sleep Onset</h3>
                  <p className="text-sm text-slate-500">When did you go to bed?</p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="space-y-3">
                  <Label className="text-slate-700 font-medium">What time did you go to bed?</Label>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-3 justify-center">
                      {/* Hour Selector */}
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500 text-center block">Hour</Label>
                        <Select 
                          value={bedTimeHour} 
                          onValueChange={(v) => {
                            setBedTimeHour(v);
                            updateBedTime(v, bedTimeMinute, bedTimePeriod);
                          }}
                        >
                          <SelectTrigger className="w-20 bg-white" data-testid="bed-time-hour">
                            <SelectValue placeholder="Hour" />
                          </SelectTrigger>
                          <SelectContent className="max-h-48 overflow-y-auto">
                            {Array.from({ length: 12 }, (_, i) => i + 1).map(hour => (
                              <SelectItem key={hour} value={hour.toString()}>
                                {hour.toString().padStart(2, '0')}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <span className="text-2xl font-bold text-slate-400 mt-5">:</span>
                      
                      {/* Minute Selector */}
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500 text-center block">Min</Label>
                        <Select 
                          value={bedTimeMinute} 
                          onValueChange={(v) => {
                            setBedTimeMinute(v);
                            updateBedTime(bedTimeHour, v, bedTimePeriod);
                          }}
                        >
                          <SelectTrigger className="w-20 bg-white" data-testid="bed-time-minute">
                            <SelectValue placeholder="Min" />
                          </SelectTrigger>
                          <SelectContent className="max-h-48 overflow-y-auto">
                            {["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"].map(minute => (
                              <SelectItem key={minute} value={minute}>
                                {minute}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* AM/PM Selector */}
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500 text-center block">Period</Label>
                        <Select 
                          value={bedTimePeriod} 
                          onValueChange={(v) => {
                            setBedTimePeriod(v);
                            updateBedTime(bedTimeHour, bedTimeMinute, v);
                          }}
                        >
                          <SelectTrigger className="w-20 bg-white" data-testid="bed-time-period">
                            <SelectValue placeholder="AM/PM" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="AM">AM</SelectItem>
                            <SelectItem value="PM">PM</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {formData.bed_time && (
                      <p className="text-center text-sm text-primary mt-3 font-medium">
                        Selected: {formData.bed_time}
                      </p>
                    )}
                  </div>
                </div>

                <Separator />

                <div className="space-y-3">
                  <Label className="text-slate-700 font-medium">How long did it take you to fall asleep?</Label>
                  <RadioGroup 
                    value={formData.sleep_latency} 
                    onValueChange={(v) => updateField("sleep_latency", v)}
                    className="space-y-2"
                  >
                    {[
                      "Immediately after getting into bed",
                      "Within 15 minutes",
                      "15–30 minutes later",
                      "30–60 minutes later",
                      "More than 1 hour later"
                    ].map(option => (
                      <RadioOption key={option} value={option} label={option} field="sleep_latency" />
                    ))}
                  </RadioGroup>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Night Awakenings */}
          {step === 4 && (
            <div className="space-y-6 animate-fadeIn">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Moon className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Night Awakenings</h3>
                  <p className="text-sm text-slate-500">Did you wake up during the night?</p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="space-y-3">
                  <Label className="text-slate-700 font-medium">How many times did you wake up during the night (not counting final awakening)?</Label>
                  <RadioGroup 
                    value={formData.wake_count} 
                    onValueChange={(v) => updateField("wake_count", v)}
                    className="space-y-2"
                  >
                    {[
                      "0 times",
                      "1 time",
                      "2 times",
                      "3 times",
                      "More than 3 times"
                    ].map(option => (
                      <RadioOption key={option} value={option} label={option} field="wake_count" />
                    ))}
                  </RadioGroup>
                </div>

                <Separator />

                <div className="space-y-3">
                  <Label className="text-slate-700 font-medium">In total, how long did these awakenings last?</Label>
                  <RadioGroup 
                    value={formData.wake_duration} 
                    onValueChange={(v) => updateField("wake_duration", v)}
                    className="space-y-2"
                  >
                    {[
                      "Less than 10 minutes",
                      "10–30 minutes",
                      "30–60 minutes",
                      "1–2 hours",
                      "More than 2 hours"
                    ].map(option => (
                      <RadioOption key={option} value={option} label={option} field="wake_duration" />
                    ))}
                  </RadioGroup>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Waking Up */}
          {step === 5 && (
            <div className="space-y-6 animate-fadeIn">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Sun className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Waking Up</h3>
                  <p className="text-sm text-slate-500">When did you wake up?</p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="space-y-3">
                  <Label className="text-slate-700 font-medium">What time was your final awakening?</Label>
                  <RadioGroup 
                    value={formData.final_wake_time} 
                    onValueChange={(v) => updateField("final_wake_time", v)}
                    className="space-y-2"
                  >
                    {[
                      "Before 5:00 AM",
                      "5:00 – 6:00 AM",
                      "6:00 – 7:00 AM",
                      "7:00 – 8:00 AM",
                      "After 8:00 AM"
                    ].map(option => (
                      <RadioOption key={option} value={option} label={option} field="final_wake_time" />
                    ))}
                  </RadioGroup>
                </div>

                <Separator />

                <div className="space-y-3">
                  <Label className="text-slate-700 font-medium">What time did you get out of bed for the day?</Label>
                  <RadioGroup 
                    value={formData.get_up_time} 
                    onValueChange={(v) => updateField("get_up_time", v)}
                    className="space-y-2"
                  >
                    {[
                      "Immediately after waking",
                      "Within 15 minutes",
                      "15–30 minutes later",
                      "30–60 minutes later",
                      "More than 1 hour later"
                    ].map(option => (
                      <RadioOption key={option} value={option} label={option} field="get_up_time" />
                    ))}
                  </RadioGroup>
                </div>
              </div>
            </div>
          )}

          {/* Step 6: Daytime & Quality */}
          {step === 6 && (
            <div className="space-y-6 animate-fadeIn">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Daytime & Quality</h3>
                  <p className="text-sm text-slate-500">How was your day and sleep quality?</p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="space-y-3">
                  <Label className="text-slate-700 font-medium">Did you take a nap during the day?</Label>
                  <RadioGroup 
                    value={formData.nap} 
                    onValueChange={(v) => updateField("nap", v)}
                    className="space-y-2"
                  >
                    {[
                      "No",
                      "Yes, less than 30 minutes",
                      "Yes, 30–60 minutes",
                      "Yes, more than 1 hour"
                    ].map(option => (
                      <RadioOption key={option} value={option} label={option} field="nap" />
                    ))}
                  </RadioGroup>
                </div>

                <Separator />

                <div className="space-y-3">
                  <Label className="text-slate-700 font-medium">How would you rate the quality of your sleep?</Label>
                  <RadioGroup 
                    value={formData.quality} 
                    onValueChange={(v) => updateField("quality", v)}
                    className="space-y-2"
                  >
                    {[
                      "Very poor",
                      "Poor",
                      "Fair",
                      "Good",
                      "Very good"
                    ].map(option => (
                      <RadioOption key={option} value={option} label={option} field="quality" />
                    ))}
                  </RadioGroup>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4 flex justify-between">
          <Button
            variant="outline"
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            data-testid="back-btn"
          >
            {step === 1 ? "Cancel" : "Back"}
          </Button>
          
          {step < totalSteps ? (
            <Button
              onClick={() => setStep(step + 1)}
              className="btn-primary"
              data-testid="next-btn"
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="btn-primary"
              data-testid="submit-entry-btn"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Saving...
                </div>
              ) : (
                "Submit Entry"
              )}
            </Button>
          )}
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SleepDiaryForm;
